import { Button } from 'component/Button'
import { Dropdown } from 'component/Dropdown/Dropdown'
import { getFilterCount, Option } from 'component/Select/Option'
import { Space } from 'component/Space'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { FilterPopover } from 'page/catalog/FilterPopover/FilterPopover'
import { FilterStore } from 'page/catalog/store/FilterStore'
import { MouseEvent, useEffect, useState } from 'react'
import css from './FilterButton.module.scss'

type Props = {
  filter: FilterStore
  options: Option[]
  buttonPadSmall?: boolean
  inline?: boolean
  onOpen?: () => void
}

export const FilterButton = observer(
  ({ filter, options, buttonPadSmall, inline, onOpen }: Props) => {
    const [showContent, setShowContent] = useState(false)

    const count = getFilterCount(filter, options)
    const add = !count && !filter.open

    const onOpened = useLatestCallback((value: boolean) => {
      if (value && onOpen) {
        onOpen()
      }

      setShowContent(value)
      return (filter.open = value)
    })

    const onClear = useLatestCallback((event: MouseEvent) => {
      event.stopPropagation()
      filter.value = []
      filter.trigger()
    })

    useEffect(() => {
      setShowContent(filter.open)
    }, [filter.open])

    const content = (
      <FilterPopover filter={filter} options={options} inline={inline} />
    )

    if (inline) {
      return (
        <div
          className={`${css.inlineContainer} ${filter.open ? css.active : ''}`}
        >
          <div className={css.inlineButtonWrapper}>
            <Button
              simple
              xl
              compact
              className={css.inlineButton}
              action={() => onOpened(!filter.open)}
            >
              <Space wide opposite>
                <Space gap={8}>
                  <span>{filter.title}</span>
                  {count > 0 ? (
                    <Space gap={4} className={css.countContainer}>
                      <span className={css.buttonCount}>
                        {count}
                      </span>
                      <div
                        className={css.clearButton}
                        onClick={onClear}
                      >
                        <Icon size={16} name="close" />
                      </div>
                    </Space>
                  ) : (
                    <div className={css.notSelected}>
                      {i18n('combo.NotSelected')}
                    </div>
                  )}
                </Space>
                <Icon
                  size={16}
                  name="keyboard_arrow_down"
                  className={`${css.arrow} ${filter.open ? css.arrowOpen : ''}`}
                />
              </Space>
            </Button>
          </div>
          {showContent && <div className={css.inlineContent}>{content}</div>}
        </div>
      )
    }

    return (
      <Dropdown
        height={32}
        content={content}
        onOpen={onOpened}
        buttonPadSmall={buttonPadSmall}
      >
        <span>{filter.title}</span>
        {add ? (
          <Icon size={16} name="add" tertiary />
        ) : (
          <Space gap={4} className={css.countContainer}>
            <span className={css.buttonCount}>{count}</span>
            {count > 0 && (
              <div
                className={css.clearButton}
                onClick={onClear}
              >
                <Icon size={12} name="close" />
              </div>
            )}
          </Space>
        )}
      </Dropdown>
    )
  },
)
